import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { MultiplayerContext, SocketEventHandler } from "./MultiplayerContext";
import {
  BackendEventType,
  EventType,
  MultiplayerBasePayloadType,
  MultiplayerEventType,
} from "../../types/Multiplayer.types";
import { useStore } from "react-redux";
import { Buffer } from "buffer";
import { InflateStream } from "zlibt2";
import DesignService from "../../service/DesignService";
import { useAuth } from "../../common/hooks/useAuth";
import { useTeam } from "../Team";
import { useDesign } from "../Design/useDesign";
import { useProject } from "../Project/useProject";

interface Props {
  children: ReactNode;
}

export const MultiplayerProvider = ({ children }: Props) => {
  const { userInfo } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<ReconnectingWebSocket | null>(null);
  const { refetchTeam } = useTeam();
  const { refetchDesign } = useDesign();
  const { refetchProject } = useProject();
  const { updateDesignImages } = useDesign();

  const eventHandlersRef = useRef<
    Map<MultiplayerEventType, Set<SocketEventHandler>>
  >(new Map());
  const { dispatch } = useStore();

  const sendMessage = useCallback(
    async (type: string, payload: any): Promise<void> => {
      if (!socketRef.current || !isConnected || !userInfo?.id) {
        return;
      }

      return new Promise((resolve, reject) => {
        try {
          socketRef.current?.send(JSON.stringify({ type, payload }));
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },
    [isConnected, userInfo?.id]
  );

  const subscribeToDesign = useCallback(
    async (designId: string | null): Promise<void> => {
      await sendMessage("subscribeToDesign", { designId });
    },
    [sendMessage]
  );

  const unsubscribeFromDesign = useCallback(async (): Promise<void> => {
    await sendMessage("subscribeToDesign", { designId: null });
  }, [sendMessage]);

  const subscribeToProject = useCallback(
    async (projectId: string | null): Promise<void> => {
      await sendMessage("subscribeToProject", { projectId });
    },
    [sendMessage]
  );

  const unsubscribeFromProject = useCallback(async (): Promise<void> => {
    await sendMessage("unsubscribeFromProject", { projectId: null });
  }, [sendMessage]);

  const subscribeToTeam = useCallback(
    async (teamId: string | null): Promise<void> => {
      await sendMessage("subscribeToTeam", { teamId });
    },
    [sendMessage]
  );

  const unsubscribeFromTeam = useCallback(async (): Promise<void> => {
    await sendMessage("unsubscribeFromTeam", { teamId: null });
  }, [sendMessage]);

  const sendCursorPosition = useCallback(
    async (
      designId: string | null,
      coordinates: { x: number; y: number }
    ): Promise<void> => {
      await sendMessage("movement", { designId, coordinates });
    },
    [sendMessage]
  );

  const handleDesignUpdate = useCallback(
    async (data: any) => {
      const newDesignStateCompressedBased64 = data.designState;
      const newDesignStateCompressedBuffer = Buffer.from(
        newDesignStateCompressedBased64,
        "base64"
      );

      const inflatedBuffer = new InflateStream(
        new Uint8Array(newDesignStateCompressedBuffer)
      );
      const decompressed = inflatedBuffer.decompress(
        new Uint8Array(newDesignStateCompressedBuffer)
      );
      const buffer = Buffer.from(decompressed.buffer);
      const str = buffer.toString("utf8");
      const newDesignState = JSON.parse(str.replace(/\0+$/, ""));

      updateDesignImages(newDesignState.images);

      // Notify design state update handlers
      const handlers = eventHandlersRef.current.get(EventType.JOIN_DESIGN);
      if (handlers) {
        handlers.forEach((handler) => handler(newDesignState));
      }

      // Update design.updatedAt in backend
      await DesignService.propagateDesignStateUpdate({
        designId: newDesignState.id,
      });
    },
    [updateDesignImages]
  );

  const handleConnection = useCallback(() => {
    if (!userInfo?.id) {
      return;
    }
    const wsUrl = process.env.REACT_APP_WEB_SOCKET || "";
    socketRef.current = new ReconnectingWebSocket(wsUrl, [], { debug: false });
  }, [userInfo?.id]);

  // Initialize socket connection
  useEffect(() => {
    if (!userInfo?.id) {
      setLoading(false);
      return;
    }
    handleConnection();

    const onOpen = () => {
      console.debug("Socket connected");
      setIsConnected(true);
      setLoading(false);
    };

    const onClose = () => {
      console.debug("Socket disconnected");
      setIsConnected(false);
    };

    const onMessage = (event: MessageEvent) => {
      try {
        //typed
        const data = JSON.parse(event.data) as {
          eventType: MultiplayerEventType;
          payload: MultiplayerBasePayloadType;
        };

        if (data.eventType === BackendEventType.DESIGN_RENAMED) {
          refetchDesign({ skipLoading: true });
          refetchProject({ skipLoading: true });
        }
        if (
          data.eventType === BackendEventType.DESIGN_CREATED ||
          data.eventType === BackendEventType.DESIGN_MOVED ||
          data.eventType === BackendEventType.DESIGN_DELETED ||
          data.eventType === BackendEventType.DESIGN_FAVORITED ||
          data.eventType === BackendEventType.DESIGN_LOCKED ||
          data.eventType === BackendEventType.DESIGN_UNLOCKED
        ) {
          refetchDesign();
          refetchProject();
        }
        if (
          data.eventType === BackendEventType.TEAM_MEMBER_LEFT ||
          data.eventType === BackendEventType.TEAM_MEMBER_ADDED ||
          data.eventType === BackendEventType.TEAM_RENAMED ||
          data.eventType === BackendEventType.TEAM_DELETED ||
          data.eventType === BackendEventType.TEAM_CREATED ||
          data.eventType === BackendEventType.USER_ADDED_TO_TEAM ||
          data.eventType === BackendEventType.USER_REMOVED_FROM_TEAM
        ) {
          refetchTeam({ skipLoading: true });
        }

        if (
          (data.eventType === BackendEventType.PROJECT_CREATED ||
            data.eventType === BackendEventType.PROJECT_RENAMED ||
            data.eventType === BackendEventType.PROJECT_DELETED ||
            data.eventType === BackendEventType.PROJECT_MOVED_TO_TEAM ||
            data.eventType === BackendEventType.PROJECT_CONVERTED_TO_TEAM ||
            data.eventType === BackendEventType.PROJECT_DUPLICATED ||
            data.eventType === BackendEventType.PROJECT_UPDATED) &&
          data.payload.teamId
        ) {
          refetchTeam({ skipLoading: true });
          refetchProject({ skipLoading: true });
        }

        if (data.eventType === BackendEventType.PROJECT_UPDATED) {
          refetchDesign();
          refetchProject();
        }

        // Handle Update Collaborators Location
        // we dont want to handle update collaborators location if the event type is cursor position or any design update event because designs updated only returns usersLocation user in this design
        if (
          eventHandlersRef.current.has(
            EventType.UPDATE_COLLABORATORS_LOCATION
          ) &&
          !(
            [
              EventType.CURSOR_POSITION,
              EventType.WHO_IS_IN_THE_DESIGN,
              EventType.JOIN_DESIGN,
              EventType.LEAVE_DESIGN,
              BackendEventType.DESIGN_RENAMED,
              BackendEventType.DESIGN_CREATED,
              BackendEventType.DESIGN_DELETED,
              BackendEventType.DESIGN_MOVED,
              BackendEventType.DESIGN_FAVORITED,
              BackendEventType.DESIGN_UPDATE,
            ] as MultiplayerEventType[]
          ).includes(data.eventType)
        ) {
          const handlers = eventHandlersRef.current.get(
            EventType.UPDATE_COLLABORATORS_LOCATION
          );
          if (handlers) {
            handlers.forEach((handler) => handler(data.payload));
          }
          if (data.eventType === EventType.UPDATE_COLLABORATORS_LOCATION) {
            return;
          }
        }

        // Handle other events based on eventType
        if (data.eventType && eventHandlersRef.current.has(data.eventType)) {
          const handlers = eventHandlersRef.current.get(data.eventType);
          if (handlers) {
            handlers.forEach((handler) => handler(data.payload));
          }
        }
      } catch (error) {
        console.error("Error processing socket message:", error);
      }
    };

    socketRef.current?.addEventListener("open", onOpen);
    socketRef.current?.addEventListener("close", onClose);
    socketRef.current?.addEventListener("message", onMessage);

    return () => {
      if (socketRef.current) {
        socketRef.current.removeEventListener("open", onOpen);
        socketRef.current.removeEventListener("close", onClose);
        socketRef.current.removeEventListener("message", onMessage);
        socketRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, dispatch, handleDesignUpdate, handleConnection]);

  const addEventListener = useCallback(
    (eventType: MultiplayerEventType, handler: SocketEventHandler) => {
      if (!eventHandlersRef.current.has(eventType)) {
        eventHandlersRef.current.set(eventType, new Set());
      }
      eventHandlersRef.current.get(eventType)?.add(handler);
    },
    []
  );

  const removeEventListener = useCallback(
    (eventType: MultiplayerEventType, handler: SocketEventHandler) => {
      eventHandlersRef.current.get(eventType)?.delete(handler);
    },
    []
  );

  return (
    <MultiplayerContext.Provider
      value={{
        loading,
        socket: socketRef.current,
        isConnected,
        addEventListener,
        removeEventListener,
        subscribeToDesign,
        unsubscribeFromDesign,
        subscribeToProject,
        unsubscribeFromProject,
        subscribeToTeam,
        unsubscribeFromTeam,
        sendCursorPosition,
      }}
    >
      {children}
    </MultiplayerContext.Provider>
  );
};
